import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { Tooltip } from '@yola/ws-ui';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import { useSelector, useDispatch } from 'react-redux';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import {
  DIALOG_BACKGROUND_TRIGGER_ID,
  SUBMIT_BUTTON_TRIGGER_ID,
} from 'src/js/modules/dialogs/constants/common';
import segment from 'src/js/modules/analytics/segment';
import helpers from '../helpers';
import actions from '../actions';
import DesignColorsDialog from '../components/design-colors-dialog';
import triggerIds from '../constants/trigger-ids';

const {
  createMapById,
  preparePaletteElement,
  sortPresets,
  readOnlyModeDecorator,
  updateCustomPalettes,
  compilePaletteIfNeeded,
  updateLivePaletteElement,
  saveGlobalPalette,
} = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  title: i18next.t('Choose a color palette'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Switch or edit color presets to instantly change the overall appearance of your site'
  ),
  heading: i18next.t('Library'),
  customPalettesTitle: i18next.t('Custom palettes'),
  paletteLabel: i18next.t('Palette'),
  customPaletteLabel: i18next.t('Custom palette'),
  editLabel: i18next.t('Settings'),
  deleteLabel: i18next.t('Delete'),
});

const DesignColorsContainer = ({
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
  sourceId,
}) => {
  const { carriedCustomPalettes, carriedActivePaletteId, offsetX, offsetY, originPalette } =
    getSharedData();

  const colorPalettes = useSelector(customization.selectors.getColorPalettes);
  const initialCustomColorPalettes = useSelector(customization.selectors.getCustomColorPalettes);
  const currentColorPalette = useSelector(customization.accessors.getCurrentColorPalette);

  const [dialogPosition, updatePosition] = useState({
    offsetX,
    offsetY,
  });

  const initialColorPaletteId = currentColorPalette.id;
  const [activeColorPaletteId, setActiveColorPaletteId] = useState(
    () => carriedActivePaletteId || initialColorPaletteId
  );

  const [customColorPalettes, setCustomColorPalettes] = useState(() =>
    sortPresets(
      carriedCustomPalettes || initialCustomColorPalettes,
      carriedActivePaletteId || initialColorPaletteId
    )
  );

  const defaultColorPalettes = useMemo(
    () => sortPresets(colorPalettes, initialColorPaletteId),
    [colorPalettes, initialColorPaletteId]
  );

  const palettesMap = useMemo(
    () => createMapById([...defaultColorPalettes, ...customColorPalettes]),
    [defaultColorPalettes, customColorPalettes]
  );

  const captions = useMemo(() => getCaptions(), []);

  const dispatch = useDispatch();

  const applyColorPalette = useCallback((palette) => {
    const { element, compiledElement: colorPaletteElement } = preparePaletteElement({
      compiledPalette: palette,
    });

    if (!element) return;

    element.replaceWith(colorPaletteElement);
  }, []);

  const handleChangeColorPalette = useCallback(
    (colorPaletteId) => {
      const palette = compilePaletteIfNeeded(palettesMap[colorPaletteId]);
      applyColorPalette(palette);
      setActiveColorPaletteId(colorPaletteId);

      if (activeColorPaletteId !== colorPaletteId) {
        dispatch(actions.switchColorPalette(palette.variables));

        trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_CHANGED, {
          dialogId: dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG,
          colorPreset: colorPaletteId,
        });
      }
    },
    [palettesMap, activeColorPaletteId, applyColorPalette, dispatch]
  );

  const updateSharedState = useCallback(
    (customData) => {
      resolveSharedData({
        offsetX: dialogPosition.offsetX,
        offsetY: dialogPosition.offsetY,
        carriedCustomPalettes: customColorPalettes,
        carriedActivePaletteId: activeColorPaletteId,
        activePalette: palettesMap[activeColorPaletteId],
        ...customData,
      });
    },
    [resolveSharedData, dialogPosition, customColorPalettes, palettesMap, activeColorPaletteId]
  );

  const addPaletteClick = useCallback(() => {
    const palette = palettesMap[activeColorPaletteId];

    updateSharedState({
      originPalette: palette,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.BASE_COLORS_PICKER_DIALOG, {
        triggerId: triggerIds.ADD_CUSTOM_PALETTE,
        colorPreset: palette.id,
      })
    );
  }, [palettesMap, activeColorPaletteId, updateSharedState, dispatch]);

  const deletePaletteClick = useCallback(
    (id) => {
      const filteredColorPalettes = customColorPalettes.filter((palette) => palette.id !== id);

      Tooltip.hide();

      trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_DELETED, {
        dialogId: dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG,
      });

      if (activeColorPaletteId === id) {
        handleChangeColorPalette(defaultColorPalettes[0].id);
      }

      setCustomColorPalettes(filteredColorPalettes);
    },
    [customColorPalettes, activeColorPaletteId, handleChangeColorPalette, defaultColorPalettes]
  );

  const changeColorPalette = (isChanged) => dispatch(actions.changeColorPalette(isChanged));

  const handleEditPaletteClick = useCallback(
    (id) => {
      const palette = palettesMap[id];

      updateSharedState({
        originPalette: palette,
      });

      dispatch(
        dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
          triggerId: triggerIds.PALETTE_SETTINGS,
          colorPreset: palette.id,
        })
      );
    },
    [palettesMap, updateSharedState, dispatch]
  );

  const onClose = () => {
    dialogs.operations.hide();
  };

  const isPaletteChanged = () => {
    if (initialColorPaletteId !== activeColorPaletteId) {
      return true;
    }

    if (!originPalette) {
      return false;
    }

    return !isEqual(originPalette, palettesMap[activeColorPaletteId]);
  };

  const handleCancel = () => {
    if (isPaletteChanged()) {
      const initialPalettesMap = createMapById([
        ...defaultColorPalettes,
        ...(initialCustomColorPalettes || []),
      ]);

      const initialPalette = compilePaletteIfNeeded(initialPalettesMap[initialColorPaletteId]);
      updateLivePaletteElement(initialPalette);
      changeColorPalette(false);
    }

    onDialogCancel({
      sourceId,
    });
    onClose();
  };

  const handleSubmit = (event) => {
    if (customColorPalettes) {
      updateCustomPalettes(initialCustomColorPalettes, customColorPalettes);
    }

    const isPaletteEdited = isPaletteChanged();

    if (isPaletteEdited) {
      const activePalette = compilePaletteIfNeeded(palettesMap[activeColorPaletteId]);
      saveGlobalPalette({ compiledPalette: activePalette });
      changeColorPalette(true);
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      colorPaletteAdjusted: isPaletteEdited,
      ...(isPaletteEdited && { colorPreset: activeColorPaletteId }),
    });
    onClose();
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const memoizedSetActivePalette = useMemo(
    () => readOnlyModeDecorator(handleChangeColorPalette),
    [handleChangeColorPalette]
  );
  const memoizedAddPaletteClick = useMemo(
    () => readOnlyModeDecorator(addPaletteClick),
    [addPaletteClick]
  );
  const memoizedDeletePaletteClick = useMemo(
    () => readOnlyModeDecorator(deletePaletteClick),
    [deletePaletteClick]
  );

  return (
    <DesignColorsDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      activePaletteId={activeColorPaletteId}
      palettes={defaultColorPalettes}
      setActivePalette={memoizedSetActivePalette}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      customPalettes={customColorPalettes}
      onAddPaletteClick={memoizedAddPaletteClick}
      onEditPaletteClick={handleEditPaletteClick}
      onDeletePaletteClick={memoizedDeletePaletteClick}
    />
  );
};

DesignColorsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default DesignColorsContainer;
